import {
  consola,
  createConsola,
  type ConsolaInstance,
  type ConsolaOptions,
  type ConsolaReporter,
} from "@-/com.npmjs.consola";
import onetime from "@-/com.npmjs.onetime";
import { isErrorLike } from "@-/com.npmjs.serialize-error";
import {
  type AppLogLevel,
  appendConsoleLogDelta,
  consolaLogLevelToAppLogLevel,
  isConsoleTransportEnabled,
  serializeAppError,
} from "#app-log-layer-common";
import { getAppLogLayer } from "#app-log-layer";
import { hasAppLogListeners } from "#app-log-listeners";

const PASS_THROUGH_CONSOLE_METHODS = new Set<PropertyKey>([
  "clear",
  "count",
  "countReset",
  "dir",
  "dirxml",
  "groupEnd",
  "profile",
  "profileEnd",
  "table",
  "time",
  "timeEnd",
  "timeLog",
  "timeStamp",
]);
const LOCATION_QUERY_OR_HASH_PATTERN = /[?#]/;

export type ImportMetaLocation =
  | null
  | string
  | undefined
  | URL
  | readonly [filename: string, line: number, column: number]
  | {
      readonly col?: number;
      readonly column?: number;
      readonly columnNumber?: number;
      readonly file?: string;
      readonly filename?: string;
      readonly line?: number;
      readonly lineNumber?: number;
      readonly url?: string;
    };

function basename(value: string): string {
  const withoutQuery = value.split(LOCATION_QUERY_OR_HASH_PATTERN, 1)[0] ?? value;
  const normalized = withoutQuery.replaceAll("\\", "/");
  return normalized.slice(normalized.lastIndexOf("/") + 1);
}

function readString(target: object, key: string): string | undefined {
  const value = Reflect.get(target, key);
  return typeof value === "string" ? value : undefined;
}

function readNumber(target: object, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = Reflect.get(target, key);
    if (typeof value === "number") {
      return value;
    }
  }
  return undefined;
}

function isUrl(value: ImportMetaLocation): value is URL {
  return typeof URL !== "undefined" && value instanceof URL;
}

/**
 * Normalizes an `import.meta.location` to its stable `[file:line:col]` string
 * (nullish → `[unknown]`). Used for log prefixes and, because it's stable per
 * callsite, as a cache key for per-location memoization (`import.meta.location`
 * compiles to a fresh object literal each evaluation, so it can't be keyed by
 * reference).
 */
export function normalizeLocation(location: ImportMetaLocation): string {
  if (location === null || location === undefined) {
    return "[unknown]";
  }

  if (typeof location === "string") {
    return location.startsWith("[") ? location : `[${location}]`;
  }

  if (Array.isArray(location)) {
    return `[${basename(location[0])}:${location[1]}:${location[2]}]`;
  }

  const filename = isUrl(location)
    ? basename(location.pathname)
    : basename(
        readString(location, "filename") ??
          readString(location, "file") ??
          readString(location, "url") ??
          "unknown",
      );

  if (isUrl(location)) {
    return `[${filename}]`;
  }

  const line = readNumber(location, "line", "lineNumber");
  const column = readNumber(location, "col", "column", "columnNumber");
  return line === undefined || column === undefined
    ? `[${filename}]`
    : `[${filename}:${line}:${column}]`;
}

function prefixedConsoleCall(method: PropertyKey, location: string, args: unknown[]): void {
  if (!isConsoleTransportEnabled()) {
    forwardProductionLog(method, location, args);
    return;
  }

  const host = globalThis.console;
  const fallback = typeof host.log === "function" ? host.log : undefined;
  const value = Reflect.get(host, method);
  const fn = typeof value === "function" ? value : fallback;
  if (fn === undefined) {
    return;
  }

  if (PASS_THROUGH_CONSOLE_METHODS.has(method)) {
    Reflect.apply(fn, host, args);
    return;
  }

  if (method === "assert") {
    const [condition = false, first, ...rest] = args;
    if (first === undefined) {
      Reflect.apply(fn, host, appendConsoleLogDelta([condition, location]));
      return;
    }
    Reflect.apply(
      fn,
      host,
      appendConsoleLogDelta([
        condition,
        typeof first === "string" ? `${location} ${first}` : location,
        ...(typeof first === "string" ? rest : [first, ...rest]),
      ]),
    );
    return;
  }

  const [first, ...rest] = args;
  if (first === undefined) {
    Reflect.apply(fn, host, appendConsoleLogDelta([location]));
    return;
  }
  Reflect.apply(
    fn,
    host,
    appendConsoleLogDelta([
      typeof first === "string" ? `${location} ${first}` : location,
      ...(typeof first === "string" ? rest : [first, ...rest]),
    ]),
  );
}

const CONSOLE_METHOD_TO_APP_LOG_LEVEL: Partial<Record<string, AppLogLevel>> = {
  debug: "debug",
  error: "error",
  exception: "error",
  info: "info",
  log: "info",
  trace: "trace",
  warn: "warn",
};

/**
 * Keep production `logger(import.meta.location)` calls flowing to the remote
 * LogLayer sinks without touching `globalThis.console`. Console-only helpers
 * such as `table`, `group`, and timers intentionally become no-ops there.
 */
function forwardProductionLog(method: PropertyKey, location: string, args: unknown[]): void {
  if (method === "assert") {
    const [condition, ...assertionArgs] = args;
    if (condition) {
      return;
    }
    emitAppLog("error", location, ["Assertion failed", ...assertionArgs]);
    return;
  }

  if (typeof method !== "string") {
    return;
  }
  const level = CONSOLE_METHOD_TO_APP_LOG_LEVEL[method];
  if (level === undefined) {
    return;
  }
  emitAppLog(level, location, args);
}

export function logger(location: ImportMetaLocation): Console {
  const normalizedLocation = normalizeLocation(location);
  return new Proxy(globalThis.console, {
    get(target, property, receiver) {
      if (typeof property !== "string") {
        return Reflect.get(target, property, receiver);
      }
      return (...args: unknown[]) => {
        prefixedConsoleCall(property, normalizedLocation, args);
      };
    },
  });
}

/**
 * Maps a database getter's `import.meta.location` argument to the `Console | null`
 * that `withDatabaseLoggerProxy` threads: a nullish location means "opt out of
 * logging" (e.g. tests call `db(null)`) and yields `null`, otherwise it builds
 * the per-callsite `logger(location)`. Keeps the nullish opt-out semantics in one
 * place so every database context factory can forward its location unchanged.
 */
export function databaseLoggerFromLocation(location: ImportMetaLocation): Console | null {
  return location == null ? null : logger(location);
}

/**
 * Split consola's `unknown[]` args into a primitive message array (strings and
 * numbers/booleans stringified) and any `Error` found among them. LogLayer's log
 * methods accept only `MessageDataType` (primitives), so objects/errors cannot be
 * passed positionally — the `Error` is surfaced via `withError()` (and serialized
 * by the shared `errorSerializer`), while non-error objects are JSON-stringified
 * into the message so no information is silently dropped from console output.
 */
function partitionReporterArgs(args: readonly unknown[]): {
  error: unknown | undefined;
  messageParts: string[];
} {
  let error: unknown | undefined;
  const messageParts: string[] = [];
  for (const arg of args) {
    if (error === undefined && (arg instanceof Error || isErrorLike(arg))) {
      error = arg;
      continue;
    }
    if (typeof arg === "string") {
      messageParts.push(arg);
    } else if (
      typeof arg === "number" ||
      typeof arg === "boolean" ||
      typeof arg === "bigint" ||
      arg === null ||
      arg === undefined
    ) {
      messageParts.push(String(arg));
    } else if (arg instanceof Error || isErrorLike(arg)) {
      messageParts.push(JSON.stringify(serializeAppError(arg)));
    } else {
      try {
        messageParts.push(JSON.stringify(arg));
      } catch {
        messageParts.push(Object.prototype.toString.call(arg));
      }
    }
  }
  return { error, messageParts };
}

function emitAppLog(
  level: AppLogLevel,
  prefix: string | undefined,
  args: readonly unknown[],
): void {
  const { error, messageParts } = partitionReporterArgs(args);
  const prefixed = prefix === undefined ? getAppLogLayer() : getAppLogLayer().withPrefix(prefix);
  const builder = error === undefined ? prefixed : prefixed.withError(error);
  builder[level](...messageParts);
}

/**
 * A consola reporter that forwards every log through the shared app
 * {@link getAppLogLayer | LogLayer} instance. LogLayer's transports own the
 * actual output: in non-production builds its console transport writes to bound
 * host console methods, and on the client its `SentryTransport` replaces the former
 * `consola-sentry-reporter` bridge. On the server the LogLayer additionally
 * tees to Axiom.
 *
 * The signature stays a `ConsolaReporter` so `setupConsola`/`createPlainConsola`
 * and their ~19 script callers are unchanged. Non-error/warn logs are still
 * dropped in production, preserving the prior reporter's behavior.
 */
export const getPlainReporter = onetime((): ConsolaReporter => ({
  log(logObj) {
    const level: AppLogLevel = consolaLogLevelToAppLogLevel(logObj);
    // Production drops info/debug consola output — unless the diagnostics recorder is listening,
    // in which case every level is worth buffering (it never reaches the console or Sentry logs
    // any differently; the listener transport is the only extra sink).
    if (
      level !== "error" &&
      level !== "fatal" &&
      level !== "warn" &&
      !isConsoleTransportEnabled() &&
      !hasAppLogListeners()
    ) {
      return;
    }
    emitAppLog(level, logObj.tag ? `[${logObj.tag}]` : undefined, logObj.args);
  },
}));

/**
 * Replaces the shared consola singleton's default reporters with the plain
 * reporter. This does not patch `globalThis.console`; only explicit consola
 * calls use this reporter.
 */
export const setupConsola = onetime(() => {
  consola.setReporters([getPlainReporter()]);
});

/**
 * Creates a consola instance preconfigured with the plain reporter, so its
 * output is free of consola's `%c%c` badge styling.
 */
export function createPlainConsola(options: Partial<ConsolaOptions> = {}): ConsolaInstance {
  setupConsola();
  return createConsola({ ...options, reporters: [getPlainReporter()] });
}
